'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useUser } from '@clerk/nextjs';
import { getApiBase } from '@/lib/apiBase';

type StockListView = 'mixed' | 'tabbed';

interface StockListViewContextType {
  stockListView: StockListView;
  setStockListView: (view: StockListView) => void;
}

const StockListViewContext = createContext<StockListViewContextType | undefined>(undefined);

export function StockListViewProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useUser();
  const [stockListView, setStockListViewState] = useState<StockListView>('mixed');
  const [hasLoadedPreference, setHasLoadedPreference] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load preference from API when user signs in
  useEffect(() => {
    if (!isSignedIn) {
      setHasLoadedPreference(false);
      return;
    }

    const loadPreference = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/preferences`);
        if (response.ok) {
          const data = await response.json();
          if (data.preferences?.stockListView) {
            setStockListViewState(data.preferences.stockListView);
          }
        }
      } catch (error) {
        console.error('Failed to load stockListView preference:', error);
      } finally {
        setHasLoadedPreference(true);
      }
    };

    loadPreference();
  }, [isSignedIn]);

  // Save preference to API (debounced)
  const savePreference = useCallback(async (newView: StockListView) => {
    if (!isSignedIn) return;

    try {
      await fetch(`${getApiBase()}/api/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stockListView: newView }),
      });
    } catch (error) {
      console.error('Failed to save stockListView preference:', error);
    }
  }, [isSignedIn]);

  // Wrapper that updates state and persists to API
  const setStockListView = useCallback((newView: StockListView) => {
    setStockListViewState(newView);

    // Only save to API if signed in and initial load is complete
    if (isSignedIn && hasLoadedPreference) {
      // Debounce the save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        savePreference(newView);
      }, 300);
    }
  }, [isSignedIn, hasLoadedPreference, savePreference]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <StockListViewContext.Provider value={{ stockListView, setStockListView }}>
      {children}
    </StockListViewContext.Provider>
  );
}

export function useStockListView() {
  const context = useContext(StockListViewContext);
  if (context === undefined) {
    throw new Error('useStockListView must be used within a StockListViewProvider');
  }
  return context;
}
